import React, { useState, useEffect } from "react";
import { Table, Input, Row, Col, Tabs, Skeleton, Button, Tooltip, Spin, Empty } from "antd";
import { SearchOutlined, FolderViewOutlined } from "@ant-design/icons";
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';

import { map, get } from "lodash";
import { recordError } from "../../../utils/logService";

import Lookupservice from "../../../services/eventsLookupService";
import apiService from "../../../services/eventsService";

const EventsList = ({ hasUpdated = false, statuses, defaultStatus }) => {

  // const [statuses, setStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState();
  const [events, setEvents] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [viewStatus, setViewStatus] = useState("View");
  const [eventLink, setEventLink] = useState("events-management/event");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [firstRecord, setFirstRecord] = useState(0);
  const [isNotSearchable, setIsNotSearchable] = useState(true);
  const [isSearchedResults, setIsSearchedResults] = useState(false);

  const [showParticipantsColumn, setShowParticipantsColumn] = useState(false);
  const [showDaysToEventColumn, setShowDaysToEventColumn] = useState(true);
  let submittedStatus = defaultStatus.key;
  const [activeKey, setActiveKey] = useState(submittedStatus);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    const selectedKey = parseInt(params?.status);
    const keyValue =
      statuses.find((status) => status.key === selectedKey) ||
      statuses.find((status) => status.key === submittedStatus);

    // Toggle 'Total No. Participants' column visibility based on status
    const shouldShowParticipants = [
      "Approved",
      "Concluded",
      "Archived",
    ].includes(keyValue.label);
    setShowParticipantsColumn(shouldShowParticipants);
    // Toggle 'Days to event' column visibility based on status
    const shouldShowDaysToEvent = ["Submitted", "In Progress"].includes(
      keyValue.label
    );
    setShowDaysToEventColumn(shouldShowDaysToEvent);

    if (!selectedKey) {
      navigate(`/events-management/status/${submittedStatus.toString()}`);
      getEventsByStatus({
        max: pageSize,
        first: firstRecord,
        statusKey: submittedStatus,
      });

      return;
    }

    if (location.state) {
      const { currentPage: cPage, pageSize: pSize } = location.state;

      const keepCurrentPage = pageSize === pSize;
      const first = keepCurrentPage
        ? (cPage - 1) * pageSize
        : (cPage - 1) * pSize;
      setPageSize(pSize);
      setCurrentPage(cPage);
      getEventsByStatus({
        statusKey: selectedKey,
        first: first,
        max: pSize,
        clearSearch: false,
      });
    } else {
      getEventsByStatus({
        max: pageSize,
        first: firstRecord,
        statusKey: selectedKey,
      });
    }
  }, [statuses.length > 0]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "1",
      sorter: (a, b) => a.name?.localeCompare(b.name),
    },
    {
      title: "Host Organization",
      dataIndex: "hostOrganisation",
      key: "2",
      sorter: (a, b) => a.hostOrganisation?.localeCompare(b.hostOrganisation),
    },

    {
      title: "Province",
      dataIndex: "province",
      key: "4",
      sorter: (a, b) => a.province?.localeCompare(b.province),
    },
    {
      title: "Date",
      dataIndex: "dates",
      key: "3",
    },
    // Conditionally render "Days to event" column
    ...(showDaysToEventColumn
      ? [
          {
            title: "Days to event",
            dataIndex: "countDownPhrase",
            key: "5",
            defaultSortOrder: "ascend",
            sorter: (a, b) => a.countDownDays - b.countDownDays,
          },
        ]
      : []),
    // Conditionally render "Total Participants" column
    ...(showParticipantsColumn
      ? [
          {
            title: "Number of Participants",
            dataIndex: "totalParticipants",
            key: "totalParticipants",
            sorter: (a, b) => a.totalParticipants - b.totalParticipants,
            // Render function to apply conditional styling and Tooltip
            render: (text, record) =>
              record.ongoing ? ( // Check if the event is ongoing
                <Tooltip title="Event is ongoing">
                  <span style={{ color: "blue", fontWeight: "bold" }}>
                    {text}
                  </span>
                </Tooltip>
              ) : (
                <span>{text}</span>
              ),
          },
        ]
      : []),
    {
      title: "View",
      dataIndex: "View",
      key: "7",
      render: (_, record, index) => {
        const currentRecord = {
          ...record,
          index: index + 1,
          currentPage: currentPage,
          pageSize: pageSize,
        };

        return (
          <>
            <Link
              to={`/${eventLink}/${record.key}`}
              state={{ from: location.pathname, data: currentRecord }}
            >
              <Button type="primary" icon={<FolderViewOutlined />}></Button>
            </Link>
          </>
        );
      },
    },
  ];

  const globalSearch = (searchValue) => {
    if (searchText === "") {
      setFilteredData(events);
      return;
    }

    const reg = new RegExp(
      searchValue.replace(/[.*+\-?^${}()|[\]\\]/g, "\\$&"),
      "gi"
    );

    var _filteredData = map(events, (record) => {
      const nameMatch = get(record, "name").match(reg);
      const hostMatch = get(record, "hostOrganisation").match(reg);
      const provinceMatch = get(record, "province").match(reg);

      if (!nameMatch && !hostMatch && !provinceMatch) {
        return null;
      }
      return record;
    }).filter((record) => record);

    setSearchText(searchValue);
    setFilteredData(_filteredData);
  };

  const getEventStatus = async () => {
    try {
      const response = await Lookupservice.getEventStatus();
      var statusData = [];
      if (response && response.status == 200) {
        const { data } = response;
        data.sort((a, b) => a.key - b.key);
        statusData = data
          .filter((status) => status.value !== "In Revision")
          .map((status) => ({
            key: status.key,
            label: status.value,
          }));

        setSelectedStatus(data[0].key);
        // setStatuses(statusData);
        getEventsByStatus({ statusKey: data[0].key });
        // Initialize daysToEvent visibility based on the first status
        const initialStatusLabel = data[0].value;
        setShowDaysToEventColumn(
          ["Submitted", "In Progress"].includes(initialStatusLabel)
        );
      } else {
        setSelectedStatus([]);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getEventsByStatus = async ({
    statusKey = selectedStatus,
    first = firstRecord,
    max = pageSize,
    searchValue = searchText,
    clearSearch = true,
  }) => {
    if (clearSearch) {
      setSearchText("");
      searchValue = null;
    }
    setTotal(0);
    try {
      setLoading(true);

      // Pass a flag or determine if participant data is needed from statusKey
      const fetchParticipants = ["Approved", "Concluded", "Archived"].includes(
        statuses.find((s) => s.key === statusKey)?.label // Find the label for the current statusKey
      );

      var actualStatusKey = statusKey ?? selectedStatus;
      setSelectedStatus(actualStatusKey);
      setActiveKey(actualStatusKey);

      const response = await apiService.getEventsByStatus({
        statusKey: statusKey,
        first: first,
        max: max,
        text: searchValue,
      });
      if (response && response.status === 200) {
        const { data } = response;

        setTotal(data?.total);
        var eventData = data.results?.map((element) => ({
          key: element.uuid.toString() + "x" + element.id,
          name: element.name,
          hostOrganisation: element.hostOrganisation,
          countDownDays: element.countDownDays,
          countDownPhrase: element.countDownPhrase,
          countDownDays: element.countDownDays,
          province: element?.province,
          dates: element.dates,
          totalParticipants: element.totalParticipants || 0,
          ongoing: element?.ongoing,
        }));
        setEvents(eventData);
        setFilteredData(eventData);
        setLoading(false);
      } else if (response && response.status === 204) {
        setLoading(false);
        setEvents([]);
        setFilteredData([]);
      } else {
        setEvents([]);
        setFilteredData([]);
      }
      setLoading(false);
    } catch (error) {
      recordError(error);
    } finally {
      setLoading(false); // Stop loading regardless of success or error
    }
    setIsSearchedResults(
      searchValue !== null && searchValue !== undefined && searchValue !== ""
    );
  };

  const onStatusChange = (key) => {
    const keyValue = statuses.find(status => status.key === key);

    setSelectedStatus(key);
    if (keyValue.label === "In Progres") {
      setViewStatus("Review");
    } else {
      setViewStatus("View");
    }
    // Toggle 'Total No. Participants' column visibility based on status
    const shouldShowParticipants = ["Approved", "Concluded", "Archived"].includes(keyValue.label);
    setShowParticipantsColumn(shouldShowParticipants);
    // Toggle 'Days to event' column visibility based on status
    const shouldShowDaysToEvent = ["Submitted", "In Progress"].includes(keyValue.label);
    setShowDaysToEventColumn(shouldShowDaysToEvent);

    setEventLink("events-management/event");
    setSelectedStatus(key);
    setActiveKey(key)

    getEventsByStatus({ statusKey: key });
    setFirstRecord(0);
    setCurrentPage(1);
    setSearchText("");
    navigate(`/events-management/status/${key}`);
  };


  const handleInputChange = (e) => {
    const { value: searchValue } = e.target;
    setSearchText(searchValue);
    setIsNotSearchable(searchValue.length === 0);

    if (searchValue === "" && isSearchedResults) {
      const selectedKey = parseInt(params?.status);
      setFirstRecord(0);
      setCurrentPage(1);
      getEventsByStatus({ statusKey: selectedKey, searchValue: searchValue, clearSearch: false, first: 0 });
    }
  };

  const onPageChange = (page, size) => {
    const keepCurrentPage = pageSize === size;
    const first = keepCurrentPage ? (page - 1) * pageSize : 0;
    const newPage = keepCurrentPage ? page : 1;

    setFirstRecord(first);
    setPageSize(size);
    setCurrentPage(newPage);

    const selectedKey = parseInt(params?.status);
    setSelectedStatus(selectedKey);

    getEventsByStatus({
      statusKey: selectedKey,
      first: first,
      max: size,
      clearSearch: false,
    });
  };

  const onRowChange = (row) => {
    setPageSize(row.pageSize);
  };

  const onShowSizeChange = (page, size) => {
    const keepCurrentPage = pageSize === size;
    const first = keepCurrentPage ? (page - 1) * pageSize : 0;
    const newPage = keepCurrentPage ? page : 1;

    setPageSize(size);
    setCurrentPage(newPage);
  };

  const handleSearchRequest = async () => {
    // Do nothing if searchText is empty on hitting enter
    if (searchText.length > 0) {
      setFirstRecord(0);
      getEventsByStatus({ first: 0, clearSearch: false });
      setIsSearchedResults(true);
    }
  };


  return (
    <>
      <div className="content">
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1000,
              flexDirection: "column",
            }}
          >
            <Spin size="large" />
            <p style={{ marginTop: 20, fontSize: 20 }}>
              <b>Please wait while the list is being loaded....</b>
            </p>
          </div>
        )}
        <Col xs={24}>
          <h1 className="center-align">Event List</h1>
        </Col>
        <Col xs={24}>
          <div>List of Events</div>
        </Col>
        <Row>
          <Col xs={14} md={8}>
            <Input
              placeholder="Search Events"
              type="text"
              className="search-bar"
              onChange={handleInputChange}
              onPressEnter={handleSearchRequest}
              onPaste={handleInputChange}
              value={searchText}
              allowClear
              size="large"
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={8} md={8}>
            <Button
              disabled={isNotSearchable}
              key="Search"
              value="Search"
              type="primary"
              onClick={handleSearchRequest}
              style={{ marginLeft: 10 }}
            >
              Search
            </Button>
          </Col>
        </Row>
        <div className="space"></div>
        <Tabs
          className="events-status-tabs"
          type="card"
          onChange={onStatusChange}
          items={statuses}
          activeKey={activeKey}
          defaultActiveKey={submittedStatus}
          key="1"
        />
        {loading ? (
          <Skeleton active avatar />
        ) : (
          <Table
            dataSource={filteredData}
            locale={{ emptyText: loading ? <Skeleton active={true} /> : <Empty />,
                            }}
            rowKey={(record) => record.key}
            columns={columns}
            onChange={onRowChange}
            loading={loading}
            bordered
            pagination={{
              defaultCurrent: 1,
              current: currentPage,
              defaultPageSize: pageSize,
              total: total,
              pageSize: pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              onChange: onPageChange,
              onShowSizeChange: onShowSizeChange,
              pageSizeOptions: [2, 5, 10, 20, 50, 100],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} ${
                  total > 1 ? "Events" : "Event"
                }`,
            }}
            size="middle"
            key="2"
          />
        )}
      </div>
    </>
  );
};
export default EventsList;
